import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getSession, getUser } from '../lib/auth';
import { supabase, isSupabaseConnected } from '../lib/supabase';

// ─── UUID validator — prevents malformed queries if session returns garbage ──
const isValidUUID = (str) =>
  typeof str === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

// ─── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status = 'pending' }) => {
  const styles = {
    success: 'bg-green-100 text-green-700 border-green-200',
    failed:  'bg-red-100 text-red-700 border-red-200',
    pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${styles[status] || styles.pending}`}>
      {label}
    </span>
  );
};

// ─── Summary Card ─────────────────────────────────────────────────────────────
const SummaryCard = ({ label, value, icon, iconClass, bgClass }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${bgClass}`}>
      <SafeIcon icon={icon} className={`text-sm ${iconClass}`} />
    </div>
    <p className="text-xs text-gray-400 font-medium">{label}</p>
    <p className="text-lg font-bold text-gray-800">{value}</p>
  </div>
);

// ─── Transaction Row ──────────────────────────────────────────────────────────
const TransactionRow = ({ record, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.04 }}
    className="px-5 py-4 flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors"
  >
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={FiIcons.FiZap} className="text-blue-500" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-gray-800">
          {record.plan_name || 'Unknown'} Plan
        </p>
        <p className="text-xs text-gray-400 font-mono truncate">
          {record.payment_id || '—'}
        </p>
      </div>
    </div>

    <div className="flex items-center gap-4 flex-shrink-0">
      <div className="text-right hidden sm:block">
        <p className="text-xs text-gray-400">Credits</p>
        <p className="text-sm font-bold text-blue-600">+{record.credits_purchased ?? 0}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-400">Amount</p>
        <p className="text-sm font-bold text-gray-800">
          ₹{Number(record.amount_spent || 0).toFixed(2)}
        </p>
      </div>
      <div className="text-right hidden md:block">
        <p className="text-xs text-gray-400">Date</p>
        <p className="text-xs font-semibold text-gray-600">
          {new Date(record.date).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
          })}
        </p>
      </div>
      <StatusBadge status={record.status} />
    </div>
  </motion.div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const BillingPage = () => {
  const [history, setHistory]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalSpend, setTotalSpend]     = useState(0);
  const [fetchError, setFetchError]     = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setFetchError('');

      // ── Guard: Supabase must be configured ──────────────────────────────
      if (!isSupabaseConnected) {
        setFetchError('Supabase is not connected. Please configure your environment variables.');
        setLoading(false);
        return;
      }

      // ── Resolve user ID from session → fallback to getUser() ────────────
      let userId = null;
      try {
        const { data: sessionData } = await getSession();
        userId = sessionData?.session?.user?.id ?? null;

        if (!userId) {
          const { data: userData } = await getUser();
          userId = userData?.user?.id ?? null;
        }
      } catch (e) {
        console.warn('BillingPage: session resolution error', e);
      }

      // ── Guard: must have a logged-in user ───────────────────────────────
      if (!userId) {
        setFetchError('You must be logged in to view billing history.');
        setLoading(false);
        return;
      }

      // ── Guard: userId must be a valid UUID before querying ──────────────
      // This prevents a malformed FK filter from hitting Supabase
      if (!isValidUUID(userId)) {
        console.error('BillingPage: resolved userId is not a valid UUID:', userId);
        setFetchError('Session user ID is invalid. Please sign out and sign in again.');
        setLoading(false);
        return;
      }

      // ── Fetch: filter by user_id (matches FK → auth.users.id) ───────────
      // RLS on the server ALSO enforces auth.uid() = user_id as a second layer
      const { data, error } = await supabase
        .from('transactions')
        .select('id, plan_name, payment_id, credits_purchased, amount_spent, date, status')
        .eq('user_id', userId)          // explicit client-side filter
        .order('date', { ascending: false });

      if (error) {
        console.error('BillingPage fetch error:', error.message, '| code:', error.code);
        if (error.code === '42P01') {
          setFetchError('The transactions table does not exist yet. Run your Supabase migrations.');
        } else if (error.code === 'PGRST301') {
          setFetchError('Permission denied. Please sign out and sign in again.');
        } else {
          setFetchError('Could not load transaction history. Please try again later.');
        }
        setLoading(false);
        return;
      }

      const records = data || [];
      setHistory(records);
      setTotalCredits(records.reduce((s, r) => s + (r.credits_purchased ?? 0), 0));
      setTotalSpend(records.reduce((s, r) => s + Number(r.amount_spent || 0), 0));
      setLoading(false);
    };

    load();
  }, []);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>

        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiCreditCard} className="text-blue-600" />
            Billing &amp; Purchase History
          </h1>
          <p className="text-sm text-gray-500 mt-1">All your credit purchases in one place.</p>
        </div>

        {/* Error Banner */}
        {fetchError && (
          <div className="mb-5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl flex items-start gap-2">
            <SafeIcon icon={FiIcons.FiAlertCircle} className="flex-shrink-0 mt-0.5" />
            <span>{fetchError}</span>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard
            label="Total Purchased"
            value={`${totalCredits} credits`}
            icon={FiIcons.FiZap}
            iconClass="text-blue-600"
            bgClass="bg-blue-100"
          />
          <SummaryCard
            label="Total Spent"
            value={`₹${totalSpend.toFixed(2)}`}
            icon={FiIcons.FiDollarSign}
            iconClass="text-green-600"
            bgClass="bg-green-100"
          />
          <SummaryCard
            label="Transactions"
            value={history.length}
            icon={FiIcons.FiFileText}
            iconClass="text-purple-600"
            bgClass="bg-purple-100"
          />
        </div>

        {/* Transactions Table */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-gray-700 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiList} className="text-gray-400" />
              Transactions
            </h2>
            <span className="text-xs text-gray-400">
              {history.length} record{history.length !== 1 ? 's' : ''}
            </span>
          </div>

          {history.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <SafeIcon icon={FiIcons.FiInbox} className="text-4xl mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No purchase history yet.</p>
              <p className="text-xs mt-1 text-gray-300">
                Your transactions will appear here after recharging.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {history.map((record, i) => (
                <TransactionRow key={record.id} record={record} index={i} />
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Payment records are secured and processed via Razorpay.
        </p>
      </motion.div>
    </div>
  );
};

export default BillingPage;